import { ReactNode, useCallback } from "react";
import { useNavigate, useBlocker } from "react-router-dom";
import { DiscardChangesDialog } from "@/components/ui/dialogs";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { OverlayHeader } from "./OverlayHeader";
import { OverlayContent } from "./OverlayContent";
import { OverlayActionBar } from "./OverlayActionBar";
import type { OverlayStateManager } from "@/hooks/useOverlayState";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface OverlayAction {
  label: string;
  onClick: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost";
}

export interface OverlayPageProps {
  /** Header title - can be string or ReactNode */
  title: string | ReactNode;
  /** Breadcrumb trail */
  breadcrumbs: BreadcrumbItem[];
  /** Optional entity reference (e.g., "#1234") */
  entityRef?: string;
  /** Track if there are unsaved changes */
  isDirty: boolean;
  /** Block all close actions during processing (e.g., save in progress) */
  isProcessing?: boolean;
  /** Close handler - called when user confirms exit */
  onClose: () => void;
  /** Primary action button (required) */
  primaryAction: OverlayAction;
  /** Secondary action button (optional - defaults to Cancel) */
  secondaryAction?: OverlayAction;
  /** Tertiary action button (optional - e.g., Save Draft) */
  tertiaryAction?: OverlayAction;
  /** 
   * Danger action button (optional - for destructive actions like Delete)
   * Renders as small text link, never competes visually with primary
   */
  dangerAction?: OverlayAction;
  /** Content to render */
  children: ReactNode;
  /** Additional class name for content area */
  contentClassName?: string;
  /** 
   * Overlay state machine instance (optional - for Success Closure Law enforcement)
   * When provided, enables advanced close control:
   * - Bypasses confirmation when state is "submitted"
   * - Respects closeReason for proper close behavior
   */
  overlayState?: OverlayStateManager;
}

/**
 * OverlayPage - Standard Side Sheet (400px max-width, responsive).
 * 
 * Now unified with Sheet component - uses Sheet + SheetContent internally
 * for consistent backdrop, shadow, radius, and animation across all overlays.
 * 
 * Canonical Standard Side Sheet Baseline:
 * - Desktop: w-full sm:max-w-[400px] sm:right-4 sm:left-auto (responsive with 16dp margin)
 * - Mobile: w-full inset-0 (full screen)
 * - Header: Content-driven height with py-4 (16dp) padding
 * - Footer: 72dp with pt-4 pb-6 (16dp top, 24dp bottom)
 * - Primary action: LEFT-aligned in footer
 * - Close X: Always present in header (right side)
 * - Backdrop: bg-black/40 (via Sheet)
 * - Shadow: shadow-lg (via Sheet)
 * - Radius: sm:rounded-l-lg (via Sheet)
 */
export function OverlayPage({
  title,
  breadcrumbs,
  entityRef,
  isDirty,
  isProcessing = false,
  onClose,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  dangerAction,
  children,
  contentClassName,
  overlayState,
}: OverlayPageProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // SUCCESS CLOSURE LAW: Never block after success
  // Block navigation only when dirty or processing AND not in submitted state
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      // CRITICAL: Check synchronous ref FIRST (survives React's async setState)
      // This fixes the race condition where navigate() is called before state updates
      if (overlayState?.isSubmittedSync?.()) return false;
      
      // Then check React state (for non-success closes)
      if (overlayState?.state === "submitted") return false;
      
      // Never block if closeReason is success or fatal-error
      if (overlayState?.closeReason === "success" || overlayState?.closeReason === "fatal-error") {
        return false;
      }
      
      return (isDirty || isProcessing) && currentLocation.pathname !== nextLocation.pathname;
    }
  );

  // Shake animation for blocked backdrop clicks
  // Uses CSS animation since panel is no longer animated via framer-motion
  const triggerShake = useCallback(() => {
    const panel = document.querySelector('[data-overlay-panel]') as HTMLElement;
    if (panel) {
      panel.classList.add('animate-shake');
      setTimeout(() => panel.classList.remove('animate-shake'), 400);
    }
  }, []);

  // Handle close with dirty/processing check (Rule 5)
  // SUCCESS CLOSURE LAW: If overlayState.state === "submitted", close immediately
  const handleClose = useCallback(() => {
    // Hard block during processing - cannot close at all
    if (isProcessing) return;
    
    // SUCCESS CLOSURE LAW: Submitted state closes immediately, NO confirmation
    // "An overlay that is closing due to success may never show a confirmation dialog. Ever."
    if (overlayState?.state === "submitted") {
      onClose();
      return;
    }
    
    // If closeReason is success or fatal-error, close immediately
    if (overlayState?.closeReason === "success" || overlayState?.closeReason === "fatal-error") {
      onClose();
      return;
    }
    
    if (isDirty) {
      // Trigger blocker by navigating - this will show the confirm dialog
      navigate(-1);
    } else {
      onClose();
    }
  }, [isDirty, isProcessing, navigate, onClose, overlayState?.state, overlayState?.closeReason]);

  // Handle backdrop click (Rule 6)
  // SUCCESS CLOSURE LAW: Submitted state closes immediately on backdrop click
  const handleBackdropClick = useCallback(() => {
    // Hard block during processing
    if (isProcessing) return;
    
    // SUCCESS CLOSURE LAW: Submitted state closes immediately
    if (overlayState?.state === "submitted") {
      onClose();
      return;
    }
    
    if (!isDirty) {
      onClose();
    } else {
      // If dirty: subtle shake to indicate blocked (Rule 6)
      triggerShake();
    }
  }, [isDirty, isProcessing, onClose, triggerShake, overlayState?.state]);

  // Handle blocker confirmation
  const handleConfirmLeave = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  }, [blocker]);

  const handleCancelLeave = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  // Default secondary action is Cancel
  const effectiveSecondaryAction = secondaryAction || {
    label: t("common.cancel"),
    onClick: handleClose,
  };

  return (
    <>
      {/* 
        Unified with Sheet component for consistent backdrop, shadow, radius, animation.
        OverlayPage is always open when rendered - uses Sheet as visual foundation only.
      */}
      <Sheet open={true} modal={true}>
        <SheetContent 
          side="right"
          className="p-0 flex flex-col"
          data-overlay-panel
          onPointerDownOutside={(e) => {
            // Prevent default Radix close behavior - we handle close logic ourselves
            e.preventDefault();
            handleBackdropClick();
          }}
          onEscapeKeyDown={(e) => {
            // Prevent default Radix close on Escape - respect dirty state
            e.preventDefault();
            if (isProcessing) return;
            
            // SUCCESS CLOSURE LAW: Submitted state closes immediately
            if (overlayState?.state === "submitted") {
              onClose();
              return;
            }
            
            if (isDirty) {
              triggerShake();
            } else {
              onClose();
            }
          }}
          onInteractOutside={(e) => {
            // Prevent any outside interaction from closing
            e.preventDefault();
          }}
        >
          {/* Sticky Header */}
          <OverlayHeader
            title={title}
            breadcrumbs={breadcrumbs}
            entityRef={entityRef}
            onClose={handleClose}
          />

          {/* Scrollable Content */}
          <OverlayContent className={contentClassName}>
            {children}
          </OverlayContent>

          {/* Sticky Action Bar */}
          <OverlayActionBar
            primaryAction={primaryAction}
            secondaryAction={effectiveSecondaryAction}
            tertiaryAction={tertiaryAction}
            dangerAction={dangerAction}
          />
        </SheetContent>
      </Sheet>

      {/* Unsaved Changes Dialog */}
      <DiscardChangesDialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open) handleCancelLeave();
        }}
        onDiscard={handleConfirmLeave}
      />
    </>
  );
}
